import React, { useState, useEffect, useRef } from 'react'
import { Spinner } from '@heroui/react'
import { StationPanelLinePhoto } from '../panels/StationPanelLinePhoto'
import { YearPeriodPicker } from './shared/YearPeriodPicker'
import { YearRangePicker } from './shared/YearRangePicker'
import { usePeriodGrafStore } from '../../store/usePeriodGrafStore'
import { useTableOrientationStore } from '../../store/useTableOrientationStore'
import { TableOrientationToggle } from './shared/TableOrientationToggle'
import {
  buildPeriodChartUrl,
  getPeriodTsIds,
  usePeriodRokData,
  usePeriodRokOdDoData,
} from '../../hooks/usePeriodGrafData'
import { useChartsToFill } from '../../hooks/shared/useToFill'
import { useAuthStore } from '../../store/useAuthStore'
import { checkSessionExpired } from '../../api/client'
import { ChartLoginPlaceholder, useChartHidden } from './shared/ChartAuthGate'
import { ChartDownloadButton } from './shared/ChartDownloadButton'
import { ChartFrame } from './shared/ChartFrame'

const CHART_BASE = import.meta.env.VITE_API_URL || '/api'
const dbg = import.meta.env.DEV ? console.debug.bind(console) : () => {}

const MONTH_CHIPS = [
  { id: '01', label: 'Led' },
  { id: '02', label: 'Úno' },
  { id: '03', label: 'Bře' },
  { id: '04', label: 'Dub' },
  { id: '05', label: 'Kvě' },
  { id: '06', label: 'Čvn' },
  { id: '07', label: 'Čvc' },
  { id: '08', label: 'Srp' },
  { id: '09', label: 'Zář' },
  { id: '10', label: 'Říj' },
  { id: '11', label: 'Lis' },
  { id: '12', label: 'Pro' },
  { id: 'ROK', label: 'Rok' },
]

const MONTHS_CS = ['Leden','Únor','Březen','Duben','Květen','Červen','Červenec','Srpen','Září','Říjen','Listopad','Prosinec']

function formatPeriodTs(ts) {
  const d = new Date(ts)
  const m = d.getUTCMonth()
  const y = d.getUTCFullYear()
  return `${MONTHS_CS[m]} ${y}`
}

function PeriodChartTooltip({ snap, plotArea, totalWidth }) {
  const tipWidth = 270
  const tipX = snap.x + 8 + tipWidth > totalWidth ? snap.x - tipWidth - 8 : snap.x + 8
  return (
    <div
      style={{ position: 'absolute', left: tipX, top: plotArea.y, pointerEvents: 'none', zIndex: 10, width: tipWidth }}
      className="bg-white border border-gray-200 rounded shadow-md text-xs"
    >
      <div className="px-2.5 py-1.5 border-b border-gray-100 font-medium text-gray-700">
        {snap.label}
      </div>
      <div className="px-2.5 py-1.5 flex flex-col gap-0.5">
        {snap.values.map((s, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <span style={{ background: s.color, width: 8, height: 8, borderRadius: '50%', flexShrink: 0, display: 'inline-block' }} />
            <span className="text-gray-600 flex-1 truncate">{s.label}</span>
            <span className="text-gray-800 font-medium ml-1 whitespace-nowrap">
              {s.value == null || (typeof s.value === 'number' && isNaN(s.value)) ? '—' : `${Number(s.value).toFixed(1)} ${s.unit}`}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

// onMeta — callback(meta|null) po každém (ne)úspěšném fetchi; kvůli meta.toFill
function PeriodChartView({ url, onMeta }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [snap, setSnap] = useState(null)
  const wrapperRef = useRef(null)
  const token = useAuthStore(s => s.token)
  const chartHidden = useChartHidden()

  useEffect(() => {
    if (chartHidden) return
    setLoading(true)
    setError(false)
    setSnap(null)
    setData(null)
    dbg('[PeriodGraf/PeriodChartView] GET', url)
    const headers = token ? { Authorization: `Bearer ${token}` } : {}
    fetch(url, { headers })
      .then(r => { if (!r.ok) { checkSessionExpired(r); throw new Error(`HTTP ${r.status}`) }; return r.json() })
      .then(d => { dbg('[PeriodGraf/PeriodChartView] loaded', d?.meta?.series?.length, 'series'); setData(d); setLoading(false); onMeta?.(d?.meta ?? null) })
      .catch(e => { dbg('[PeriodGraf/PeriodChartView] ERROR', e.message); setError(true); setLoading(false); onMeta?.(null) })
    // onMeta je stabilní vůči url (key komponenty obsahuje url) — záměrně mimo deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, token, chartHidden])

  function handleMove(e) {
    if (!data?.meta) return
    const { series } = data.meta
    const formatMeta = data.svg ? data.meta.svg : data.png ? data.meta.png : data.jpg ? data.meta.jpg : data.meta.gif
    if (!formatMeta) return
    const { plotArea } = formatMeta

    const allData = series.filter(s => s.data && s.data.length > 0)
    if (allData.length === 0) return
    const n = allData[0].data.length
    if (n === 0) return

    const rect = wrapperRef.current.getBoundingClientRect()
    const mouseX = e.clientX - rect.left

    if (mouseX < plotArea.x || mouseX > plotArea.x + plotArea.width) {
      setSnap(null)
      return
    }

    // Snap na nejbližší kategorii — preferuj categoryTicks z API (přesné pixel pozice středů)
    let clampedIdx
    if (formatMeta.categoryTicks && formatMeta.categoryTicks.length === n) {
      const ticks = formatMeta.categoryTicks
      clampedIdx = ticks.reduce((best, x, i) =>
        Math.abs(x - mouseX) < Math.abs(ticks[best] - mouseX) ? i : best, 0)
    } else {
      const step = plotArea.width / n
      const idx = Math.round((mouseX - plotArea.x - step / 2) / step)
      clampedIdx = Math.max(0, Math.min(n - 1, idx))
    }

    const snapX = (formatMeta.categoryTicks && formatMeta.categoryTicks.length === n)
      ? formatMeta.categoryTicks[clampedIdx]
      : plotArea.x + (plotArea.width / n) * clampedIdx + (plotArea.width / n) / 2

    // Normálová řada (Rok Od-Do) má jediný bod platný pro všechny roky — neindexovat podle clampedIdx.
    const values = allData.map(s => ({
      id: s.id,
      label: s.label,
      unit: s.unit,
      color: s.color,
      value: (s.data.length === 1 ? s.data[0] : s.data[clampedIdx])?.[1] ?? null,
    }))

    const ts = allData[0].data[clampedIdx]?.[0]
    const label = ts != null ? formatPeriodTs(ts) : String(clampedIdx + 1)

    setSnap({ x: snapX, label, values })
  }

  if (chartHidden) return <ChartLoginPlaceholder />
  if (loading) return <div className="flex items-center gap-2 text-sm text-gray-500 py-4"><Spinner size="sm" /> Načítání grafu…</div>
  if (error) return <p className="text-red-500 text-sm py-4">Chyba při načítání grafu</p>

  const { svg, png, jpg, meta } = data
  const w = meta?.context?.width || 'auto'
  const h = meta?.context?.height || 'auto'
  const formatMeta = svg ? meta?.svg : png ? meta?.png : jpg ? meta?.jpg : meta?.gif
  const plotArea = formatMeta?.plotArea
  const imgSrc = png ? `data:image/png;base64,${png}` : jpg ? `data:image/jpeg;base64,${jpg}` : null

  return (
    <div ref={wrapperRef} style={{ position: 'relative', width: w, height: h, display: 'inline-block' }}>
      {svg
        ? <div dangerouslySetInnerHTML={{ __html: svg }} />
        : imgSrc
          ? <img src={imgSrc} alt="Graf" width={w} height={h} style={{ display: 'block' }} />
          : null
      }
      <ChartDownloadButton data={data} />
      {plotArea && (
        <div
          style={{ position: 'absolute', left: plotArea.x, top: plotArea.y, width: plotArea.width, height: plotArea.height, cursor: 'crosshair' }}
          onMouseMove={handleMove}
          onMouseLeave={() => setSnap(null)}
        />
      )}
      {snap && plotArea && (
        <>
          <div style={{
            position: 'absolute', left: snap.x, top: plotArea.y,
            width: 1, height: plotArea.height,
            background: 'rgba(0,0,0,0.45)', pointerEvents: 'none',
          }} />
          <PeriodChartTooltip snap={snap} plotArea={plotArea} totalWidth={w} />
        </>
      )}
    </div>
  )
}

// Sdílené utility pro tabulky Data (styl sjednocený s MlyGrid / YlyStatsGrid)
const PG_MONTHS = ['01','02','03','04','05','06','07','08','09','10','11','12']
const PG_MONTH_LABELS = ['Led','Úno','Bře','Dub','Kvě','Čvn','Čvc','Srp','Zář','Říj','Lis','Pro']
const pgCb = 'border-b border-r border-gray-200 px-1 py-0.5 text-xs tabular-nums whitespace-nowrap'
const pgTh = 'border-b border-b-gray-200 border-r border-r-gray-200 px-1 py-1 text-xs font-normal text-center bg-gray-50'
// hlavičkový th s tmavou spodní čarou (stejná jako gray-400 pod sticky sloupcem "Veličina")
const pgTh2 = `${pgTh} border-b-gray-400`

function pgFmt(v, decimals) {
  if (v == null) return '—'
  if (decimals === 0) return String(Math.round(v))
  return v.toFixed(decimals ?? 1)
}

// Tabulka pro Režim ROK.
//   normal      — řádky = metriky (+ normály jako modré podřádky), sloupce = 12 měsíců + Rok
//   transposed  — řádky = měsíce + Rok, sloupce = metriky
function RokDataGrid({ rowDefs, monthDataMap, yearDataMap, monthNormalsMap, yearNormalMap, orientation = 'normal' }) {
  const getVal = (row, sid) => (row.isNorm ? monthNormalsMap?.[row.tsId]?.[sid] : monthDataMap?.[row.tsId]?.[sid])
  const getYear = (row) => (row.isNorm ? yearNormalMap?.[row.tsId] : yearDataMap?.[row.tsId])

  if (orientation === 'transposed') {
    const sids = [...PG_MONTHS, 'ROK']
    return (
      <div className="h-full overflow-hidden border border-gray-200">
        <div className="h-full overflow-auto">
          <table className="border-separate border-spacing-0 border-r-2 border-r-gray-400 border-b-2 border-b-gray-400 text-xs">
            <thead className="sticky top-0 z-20">
              <tr>
                <th className="sticky left-0 z-30 bg-gray-50 border-t-2 border-t-gray-400 border-l border-l-gray-400 border-b border-b-gray-400 border-r border-r-gray-400 px-3 py-1 text-left text-gray-600 font-semibold min-w-[6rem] whitespace-nowrap">Období</th>
                {rowDefs.map((row, ri) => (
                  <th key={ri} className={`${pgTh} border-t-2 border-t-gray-400 border-b-gray-400 ${row.isNorm ? 'text-blue-500' : 'text-gray-500'}`}>
                    {row.label}{row.unit && <span className="text-gray-400 ml-1 text-[10px]">({row.unit})</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sids.map((sid) => {
                const isYear = sid === 'ROK'
                const topRule = isYear ? 'border-t-2 border-t-gray-400' : ''
                return (
                  <tr key={sid} className="group hover:bg-slate-50">
                    <td className={`sticky left-0 z-10 bg-white group-hover:bg-slate-50 border-b border-l border-l-gray-400 border-r border-r-gray-400 group-last:border-b-0 px-3 py-0.5 text-left whitespace-nowrap ${isYear ? 'font-semibold text-gray-700' : 'text-gray-600'} ${topRule}`}>
                      {isYear ? 'Rok' : PG_MONTH_LABELS[PG_MONTHS.indexOf(sid)]}
                    </td>
                    {rowDefs.map((row, ri) => {
                      const v = isYear ? getYear(row) : getVal(row, sid)
                      return (
                        <td key={ri} className={`${pgCb} text-right group-last:border-b-0 ${topRule} ${row.isNorm ? 'text-blue-500' : v == null ? 'text-gray-300' : 'text-gray-700'} bg-white group-hover:bg-slate-50`}>
                          {pgFmt(v, row.decimals)}
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-hidden border border-gray-200">
      <div className="h-full overflow-auto">
        <table className="border-separate border-spacing-0 border-r-2 border-r-gray-400 border-b-2 border-b-gray-400 text-xs">
          <thead className="sticky top-0 z-20">
            <tr>
              <th className="sticky left-0 z-30 bg-gray-50 border-t-2 border-t-gray-400 border-l border-l-gray-400 border-b border-b-gray-400 border-r border-r-gray-400 px-3 py-1 text-left text-gray-600 font-semibold min-w-[16rem] whitespace-nowrap">Veličina</th>
              {PG_MONTH_LABELS.map((m, i) => <th key={i} className={`${pgTh2} border-t-2 border-t-gray-400 text-gray-500`}>{m}</th>)}
              <th className={`${pgTh2} border-t-2 border-t-gray-400 text-gray-500 border-l border-l-gray-400`}>Rok</th>
            </tr>
          </thead>
          <tbody>
            {rowDefs.map((row, ri) => {
              const isNorm = !!row.isNorm
              const tdLabelCls = isNorm
                ? 'sticky left-0 z-10 bg-blue-50 border-b border-l border-l-gray-400 border-r border-r-gray-400 px-3 py-0.5 text-left text-blue-500 whitespace-nowrap text-[10px] min-w-[16rem]'
                : 'sticky left-0 z-10 bg-white group-hover:bg-slate-50 border-b border-l border-l-gray-400 border-r border-r-gray-400 px-3 py-0.5 text-left text-gray-700 whitespace-nowrap min-w-[16rem]'
              const tdValCls = isNorm ? `${pgCb} text-right text-blue-500 bg-blue-50` : `${pgCb} text-right group-hover:bg-slate-50`

              return (
                <tr key={`${ri}-${row.tsId}`} className={isNorm ? 'bg-blue-50' : 'group hover:bg-slate-50'}>
                  <td className={tdLabelCls}>
                    {row.label}{row.unit && <span className="text-gray-400 ml-1.5 text-[10px]">({row.unit})</span>}
                  </td>
                  {PG_MONTHS.map(sid => {
                    const v = getVal(row, sid)
                    return <td key={sid} className={`${tdValCls} ${!isNorm && v == null ? 'text-gray-300' : ''}`}>{pgFmt(v, row.decimals)}</td>
                  })}
                  <td className={`${tdValCls} border-l border-l-gray-400`}>{pgFmt(getYear(row), row.decimals)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// Tabulka pro Režim ROK_OD_DO — sloupce = roky, skupiny řádků per chip
const CHIP_ORDER = ['01','02','03','04','05','06','07','08','09','10','11','12','ROK']

function RokOdDoDataGrid({ rowDefs, chipDataMap, chipNormalsMap, selectedChips, yearFrom, yearTo, orientation = 'normal' }) {
  const years = []
  for (let y = yearFrom; y <= yearTo; y++) years.push(String(y))
  const sortedChips = [...selectedChips].sort((a, b) => CHIP_ORDER.indexOf(a) - CHIP_ORDER.indexOf(b))

  const chipLabels = { ROK: 'Rok', ...Object.fromEntries(MONTH_CHIPS.map(c => [c.id, c.label])) }
  const getVal = (chip, row, y) => (row.isNorm ? chipNormalsMap?.[chip]?.[row.tsId] : chipDataMap?.[chip]?.[row.tsId]?.[y])

  if (orientation === 'transposed') {
    // řádky = roky, sloupce = (chip × metrika); 1. úroveň hlavičky = chip, 2. = metrika
    const cols = sortedChips.flatMap(chip => rowDefs.map((row, ri) => ({ chip, row, key: `${chip}-${ri}` })))
    return (
      <div className="h-full overflow-hidden border border-gray-200">
        <div className="h-full overflow-auto">
          <table className="border-separate border-spacing-0 border-r-2 border-r-gray-400 border-b-2 border-b-gray-400 text-xs">
            <thead className="sticky top-0 z-20">
              <tr>
                <th rowSpan={2} className="sticky left-0 z-30 bg-gray-50 border-t-2 border-t-gray-400 border-l border-l-gray-400 border-b border-b-gray-400 border-r border-r-gray-400 px-3 py-1 text-left align-bottom text-gray-600 font-semibold whitespace-nowrap">Rok</th>
                {sortedChips.map((chip, i) => (
                  <th key={chip} colSpan={rowDefs.length} className={`${pgTh} border-t-2 border-t-gray-400 text-gray-600 font-semibold ${i > 0 ? 'border-l border-l-gray-400' : ''}`}>
                    {chipLabels[chip] ?? chip}
                  </th>
                ))}
              </tr>
              <tr>
                {sortedChips.flatMap((chip, ci) => rowDefs.map((row, ri) => (
                  <th key={`${chip}-${ri}`} className={`${pgTh} border-b-gray-400 ${row.isNorm ? 'text-blue-500' : 'text-gray-500'} ${ci > 0 && ri === 0 ? 'border-l border-l-gray-400' : ''}`}>
                    {row.label}{row.unit && <span className="text-gray-400 ml-1 text-[10px]">({row.unit})</span>}
                  </th>
                )))}
              </tr>
            </thead>
            <tbody>
              {years.map(y => (
                <tr key={y} className="group hover:bg-slate-50">
                  <td className="sticky left-0 z-10 bg-white group-hover:bg-slate-50 border-b border-l border-l-gray-400 border-r border-r-gray-400 group-last:border-b-0 px-3 py-0.5 text-left font-semibold text-gray-700 whitespace-nowrap">{y}</td>
                  {cols.map(({ chip, row }, idx) => {
                    const firstOfChip = idx > 0 && cols[idx - 1].chip !== chip
                    const v = getVal(chip, row, y)
                    return (
                      <td key={`${chip}-${idx}`} className={`${pgCb} text-right group-last:border-b-0 ${firstOfChip ? 'border-l border-l-gray-400' : ''} ${row.isNorm ? 'text-blue-500' : v == null ? 'text-gray-300' : 'text-gray-700'} bg-white group-hover:bg-slate-50`}>
                        {pgFmt(v, row.decimals)}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-hidden border border-gray-200">
      <div className="h-full overflow-auto">
        <table className="border-separate border-spacing-0 border-r-2 border-r-gray-400 border-b-2 border-b-gray-400 text-xs">
          <thead className="sticky top-0 z-20">
            <tr>
              <th className="sticky left-0 z-30 bg-gray-50 border-t-2 border-t-gray-400 border-l border-l-gray-400 border-b border-b-gray-400 border-r border-r-gray-400 px-3 py-1 text-left text-gray-600 font-semibold min-w-[16rem] whitespace-nowrap">Veličina</th>
              {years.map(y => <th key={y} className={`${pgTh2} border-t-2 border-t-gray-400 text-gray-500`}>{y}</th>)}
            </tr>
          </thead>
          <tbody>
            {sortedChips.map(chip => (
              <React.Fragment key={chip}>
                <tr>
                  <td className="sticky left-0 z-10 bg-gray-100 border-b border-l border-l-gray-400 border-r border-r-gray-400 px-3 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap min-w-[16rem]">
                    {chipLabels[chip] ?? chip}
                  </td>
                  {years.map(y => <td key={y} className={`${pgCb} bg-gray-100`} />)}
                </tr>
                {rowDefs.map((row, ri) => {
                  const isNorm = !!row.isNorm
                  const tdLabelCls = isNorm
                    ? 'sticky left-0 z-10 bg-blue-50 border-b border-l border-l-gray-400 border-r border-r-gray-400 px-3 py-0.5 text-left text-blue-500 whitespace-nowrap text-[10px] min-w-[16rem]'
                    : 'sticky left-0 z-10 bg-white group-hover:bg-slate-50 border-b border-l border-l-gray-400 border-r border-r-gray-400 px-3 py-0.5 text-left text-gray-700 whitespace-nowrap min-w-[16rem]'
                  const tdValCls = isNorm ? `${pgCb} text-right text-blue-500 bg-blue-50` : `${pgCb} text-right group-hover:bg-slate-50`

                  return (
                    <tr key={`${chip}-${ri}-${row.tsId}`} className={isNorm ? 'bg-blue-50' : 'group hover:bg-slate-50'}>
                      <td className={tdLabelCls}>
                        {row.label}{row.unit && <span className="text-gray-400 ml-1.5 text-[10px]">({row.unit})</span>}
                      </td>
                      {years.map(y => {
                        const v = getVal(chip, row, y)
                        return <td key={y} className={`${tdValCls} ${!isNorm && v == null ? 'text-gray-300' : ''}`}>{pgFmt(v, row.decimals)}</td>
                      })}
                    </tr>
                  )
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// Výchozí definice řádků pro každý chartType.
// isNorm: true → hodnota z /sndata_ts (atribut average), jinak /sdata_ts (atribut value)
const ROW_DEFS = {
  ts_month: [
    { tsId: 'TEMPAVG_AVG',   label: 'Průměrná teplota',          unit: '°C', decimals: 1 },
    { tsId: 'TEMPAVG_AVG',   label: 'Průměrná teplota — normál', unit: '°C', decimals: 1, isNorm: true },
    { tsId: 'TNMIN_MIN',     label: 'Minimum Minimální teploty',          unit: '°C', decimals: 1 },
    { tsId: 'TNMIN_AVG',     label: 'Ø Minimální teplota',          unit: '°C', decimals: 1 },
    { tsId: 'TNMIN_AVG',     label: 'Ø Minimální teplota — normál', unit: '°C', decimals: 1, isNorm: true },
    { tsId: 'TXMAX_MAX',     label: 'Maximum Maximální teploty',          unit: '°C', decimals: 1 },
    { tsId: 'TXMAX_AVG',     label: 'Ø Maximální teplota',          unit: '°C', decimals: 1 },
    { tsId: 'TXMAX_AVG',     label: 'Ø Maximální teplota — normál', unit: '°C', decimals: 1, isNorm: true },
    { tsId: 'PRECIPSUM_SUM', label: 'Srážky',                     unit: 'mm', decimals: 1 },
  ],  
  dmdatat: [
    { tsId: 'TEMPAVG_AVG',   label: 'Průměrná teplota',          unit: '°C', decimals: 1 },
    { tsId: 'TEMPAVG_AVG',   label: 'Průměrná teplota — normál', unit: '°C', decimals: 1, isNorm: true },
    { tsId: 'TNMIN_MIN',     label: 'Minimum Minimální teploty',          unit: '°C', decimals: 1 },
    { tsId: 'TNMIN_AVG',     label: 'Ø Minimální teplota',          unit: '°C', decimals: 1 },
    { tsId: 'TNMIN_AVG',     label: 'Ø Minimální teplota — normál', unit: '°C', decimals: 1, isNorm: true },
    { tsId: 'TXMAX_MAX',     label: 'Maximum Maximální teploty',          unit: '°C', decimals: 1 },
    { tsId: 'TXMAX_AVG',     label: 'Ø Maximální teplota',          unit: '°C', decimals: 1 },
    { tsId: 'TXMAX_AVG',     label: 'Ø Maximální teplota — normál', unit: '°C', decimals: 1, isNorm: true },
  ],
  dmdatatep: [
    { tsId: 'TEMPAVG_AVG', label: 'Průměrná teplota',          unit: '°C', decimals: 1 },
    { tsId: 'TEMPAVG_AVG', label: 'Průměrná teplota — normál', unit: '°C', decimals: 1, isNorm: true },
  ],
  dmdatasra: [
    { tsId: 'PRECIPSUM_SUM', label: 'Srážky',          unit: 'mm', decimals: 1 },
    { tsId: 'PRECIPSUM_SUM', label: 'Srážky — normál', unit: 'mm', decimals: 1, isNorm: true },
  ],
  dmdatasno: [
    { tsId: 'SNOWNEW_MAX', label: 'Max. nový sníh', unit: 'cm', decimals: 1 },
    { tsId: 'SNOWNEW_MAX', label: 'Max. nový sníh — normál', unit: 'cm', decimals: 1, isNorm: true },    
  ],
  dmdatasce: [
    { tsId: 'SNOW_MAX', label: 'Max. výška sněhu', unit: 'cm', decimals: 1 },
    { tsId: 'SNOW_MAX', label: 'Max. výška sněhu — normál', unit: 'cm', decimals: 1, isNorm: true },      
  ],
  dmdatassv: [
    { tsId: 'SUNSHINESUM_SUM', label: 'Sluneční svit',          unit: 'hod', decimals: 1 },
    { tsId: 'SUNSHINESUM_SUM', label: 'Sluneční svit — normál', unit: 'hod', decimals: 1, isNorm: true },
  ],
}

export function PeriodGrafView({ station, chartType, slug = `periodgraf-${chartType}` }) {
  const orientation = useTableOrientationStore(s => s.getOrientation(slug))
  const {
    mode, setMode,
    year, setYear,
    yearFrom, setYearFrom,
    yearTo, setYearTo,
    multiSelect, setMultiSelect,
    selectedChips, setSelectedChips,
    activeTab, setActiveTab,
    chartFormat, setChartFormat,
    interactive, setInteractive,
  } = usePeriodGrafStore()

  const token = useAuthStore(s => s.token)
  const rowDefs = ROW_DEFS[chartType] ?? []
  const { dataTsIds, normTsIds } = getPeriodTsIds(rowDefs)

  // Záložka Data — ROK
  const {
    monthDataMap, yearDataMap, monthNormalsMap, yearNormalMap,
    isLoading: rokLoading, isError: rokError,
  } = usePeriodRokData(
    activeTab === 'data' && mode === 'ROK' ? station?.gh_id : null,
    year, dataTsIds, normTsIds
  )

  // Záložka Data — ROK_OD_DO
  const {
    chipDataMap, chipNormalsMap,
    isLoading: rokOdDoLoading, isError: rokOdDoError,
  } = usePeriodRokOdDoData(
    activeTab === 'data' && mode === 'ROK_OD_DO' ? station?.gh_id : null,
    yearFrom, yearTo, dataTsIds, normTsIds, selectedChips
  )

  const isLoading = mode === 'ROK' ? rokLoading : rokOdDoLoading
  const isError = mode === 'ROK' ? rokError : rokOdDoError

  // Sestavení URL grafů
  // Grafy se vždy stahují jako json[<fmt>,meta] (kvůli meta.toFill). URL = klíč grafu
  // (bez cache-bustu); každý graf se přenačte sám, až se dotáhnou všechny jeho řady.
  const format = `json[${chartFormat},meta]`

  function getChartUrls() {
    if (!station) return []
    if (mode === 'ROK') {
      const url = buildPeriodChartUrl(station.gh_id, 'ROK', { year }, chartType, format)
      dbg('[PeriodGraf/getChartUrls] ROK', url)
      return [{ key: 'rok', url }]
    }
    const sorted = [...selectedChips].sort((a, b) => CHIP_ORDER.indexOf(a) - CHIP_ORDER.indexOf(b))
    const urls = sorted.map(chip => ({
      key: chip,
      url: buildPeriodChartUrl(station.gh_id, 'ROK_OD_DO', { yearFrom, yearTo, chip }, chartType, format),
      label: MONTH_CHIPS.find(c => c.id === chip)?.label ?? chip,
    }))
    dbg('[PeriodGraf/getChartUrls] ROK_OD_DO', urls.map(u => u.url))
    return urls
  }

  const chartUrlsRef = useRef([])
  const currentUrls = getChartUrls()
  if (activeTab === 'graf') chartUrlsRef.current = currentUrls
  const chartUrls = chartUrlsRef.current

  const screenKey = JSON.stringify(['period-graf', chartType, station?.gh_id, mode, year, yearFrom, yearTo, chartUrls.map(c => c.key)])
  const { setMeta, bust } = useChartsToFill(
    screenKey,
    chartUrls.map(c => c.url),
    { gran: 'MONTH', live: false },
    activeTab === 'graf'
  )

  function toggleChip(id) {
    if (multiSelect) {
      setSelectedChips(
        selectedChips.includes(id)
          ? selectedChips.filter(c => c !== id)
          : [...selectedChips, id]
      )
    } else {
      setSelectedChips([id])
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-white flex flex-col gap-3">
        {station && <StationPanelLinePhoto station={station} />}

        <div className="flex flex-wrap items-start gap-4">
          {/* Přepínač Rok / Rok Od-Do */}
          <div className="flex flex-col gap-1">
            <span className="text-xs text-gray-400 font-medium uppercase tracking-wide">Režim</span>
            <div className="flex gap-1">
              {[{ id: 'ROK', label: 'Rok' }, { id: 'ROK_OD_DO', label: 'Rok Od-Do' }].map(m => (
                <button
                  key={m.id}
                  onClick={() => setMode(m.id)}
                  className={`px-2.5 py-0.5 rounded text-xs font-medium transition-colors border ${
                    mode === m.id
                      ? 'bg-blue-100 text-blue-700 border-blue-300'
                      : 'text-gray-500 border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {/* Picker */}
          {mode === 'ROK'
            ? <YearPeriodPicker year={year} onYearChange={setYear} obsType="DLY" />
            : <YearRangePicker yearFrom={yearFrom} yearTo={yearTo} onYearFromChange={setYearFrom} onYearToChange={setYearTo} />
          }

          {/* Formát grafu (jen tab Graf) / Transponovat (jen tab Data) */}
          <div className="flex flex-col gap-1 ml-auto">
            <span className="text-xs text-gray-400 font-medium uppercase tracking-wide">
              {activeTab === 'data' ? 'Tabulka' : 'Formát'}
            </span>
            {activeTab === 'data' ? (
              <TableOrientationToggle slug={slug} />
            ) : (
              <div className="flex items-center gap-3">
                {['png', 'jpg', 'svg'].map(fmt => (
                  <label key={fmt} className="flex items-center gap-1 text-xs text-gray-600 cursor-pointer">
                    <input type="radio" name="pgChartFormat" checked={chartFormat === fmt} onChange={() => setChartFormat(fmt)} className="accent-blue-600" />
                    {fmt.toUpperCase()}
                  </label>
                ))}
                <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer ml-2 border-l pl-3 border-gray-200">
                  <input type="checkbox" checked={interactive} onChange={e => setInteractive(e.target.checked)} className="accent-blue-600" />
                  Interaktivní
                </label>
              </div>
            )}
          </div>
        </div>

        {/* Chips + Násobný výběr (jen ROK_OD_DO) */}
        {mode === 'ROK_OD_DO' && (
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer mr-1">
              <input type="checkbox" checked={multiSelect} onChange={e => setMultiSelect(e.target.checked)} className="accent-blue-600" />
              Násobný výběr
            </label>
            <div className="flex flex-wrap gap-1">
              {MONTH_CHIPS.map(chip => {
                const isActive = selectedChips.includes(chip.id)
                return (
                  <button
                    key={chip.id}
                    onClick={() => toggleChip(chip.id)}
                    className={`px-2 py-0.5 rounded-full text-xs transition-colors border ${
                      isActive
                        ? 'bg-blue-100 text-blue-700 border-blue-300'
                        : 'text-gray-600 border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    {chip.label}
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {/* Záložky */}
        <div className="flex gap-2 border-b border-gray-100 -mb-4 pb-0">
          {['graf', 'data'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-1.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab === 'graf' ? 'Graf' : 'Data'}
            </button>
          ))}
        </div>
      </div>

      {/* Obsah */}
      <div className="flex-1 min-h-0 p-4 flex flex-col gap-4 overflow-auto">
        {activeTab === 'graf' && (
          <>
            {chartUrls.length === 0 && (
              <p className="text-sm text-gray-400">Vyberte alespoň jeden chip.</p>
            )}
            {chartUrls.map(({ key, url: chartKey, label }) => {
              const url = chartKey + bust(chartKey)
              const onMeta = meta => setMeta(chartKey, meta)
              return (
                <div key={`${key}:${token ?? 'anon'}`}>
                  {label && <div className="text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wide">{label}</div>}
                  {interactive
                    ? <PeriodChartView key={`${url}:${token ?? 'anon'}`} url={url} onMeta={onMeta} />
                    : <ChartFrame key={`${url}:${token ?? 'anon'}`} url={url} interactive={false} onMeta={onMeta} />
                  }
                </div>
              )
            })}
          </>
        )}

        {activeTab === 'data' && (
          <>
            {isLoading && <div className="flex items-center gap-2 text-sm text-gray-500"><Spinner size="sm" /> Načítání dat…</div>}
            {isError && <p className="text-red-500 text-sm">Chyba při načítání dat</p>}
            {!isLoading && !isError && mode === 'ROK' && (
              <RokDataGrid
                rowDefs={rowDefs}
                monthDataMap={monthDataMap}
                yearDataMap={yearDataMap}
                monthNormalsMap={monthNormalsMap}
                yearNormalMap={yearNormalMap}
                orientation={orientation}
              />
            )}
            {!isLoading && !isError && mode === 'ROK_OD_DO' && (
              selectedChips.length === 0
                ? <p className="text-sm text-gray-400">Vyberte alespoň jeden chip.</p>
                : <RokOdDoDataGrid
                    rowDefs={rowDefs}
                    chipDataMap={chipDataMap}
                    chipNormalsMap={chipNormalsMap}
                    selectedChips={selectedChips}
                    yearFrom={yearFrom}
                    yearTo={yearTo}
                    orientation={orientation}
                  />
            )}
          </>
        )}
      </div>
    </div>
  )
}
